import { useHippoText } from '../../hooks/useHippoText';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { coverableToExternalCase } from '../../utils/externalCasesUtil';
import { useRecoilValue } from 'recoil';
import { externalCasesAtom } from '../../recoil/atoms/externalCasesAtom';
import Currency from '../../shared_components/Currency/Currency';
import SpinOffMessage from '../CheckoutPage/components/SpinOffMessage';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { paymentFrequencyMap, periodicityUnit } from '../../utils/currencyUtil';
import { coverableEffectiveDate, toLongDateString } from '../../utils/dateUtil';
import {
    isAccCoverableForOtherPerson,
    shouldShowConditionReportWarning,
    shouldShowUvpWarning
} from '../../utils/coverablesUtil';
import type { PrhCommonDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';
import { FLAVOUR_NAME_MAP, LOB_CODE } from '../../constants/coverable';
import { Message } from '@flos/react-ui';
import DOMPurify from 'dompurify';

const CoverableInfoList = ({ coverables }: { coverables: CoverableDTO[] }): React.JSX.Element => {
    const getHippoText = useHippoText();
    const externalCases = useRecoilValue(externalCasesAtom);
    const jobData = useRecoilValue(jobAtom);

    const selectedPaymentFrequency =
        jobData.selectedPaymentFrequency ?? paymentFrequencyMap.monthly;
    const paymentFrequencyPostfix = periodicityUnit[selectedPaymentFrequency];
    const lastIteration = coverables.length - 1;

    const isHouseWithUWIAndBuildingReport = (coverable: CoverableDTO): boolean | undefined => {
        return (
            coverable.lobCode === LOB_CODE.PRIVATE_HOUSE &&
            (coverable as PrhCommonDTO)?.hasUWI &&
            (coverable as PrhCommonDTO)?.outputBuildingReportPending
        );
    };

    const SpinOffDescriptionText = (coverable: CoverableDTO): string => {
        if (coverable.flavourName.toLocaleLowerCase() === FLAVOUR_NAME_MAP.top_mvp_car)
            return getHippoText('DSS-RECEIPT-CAR-UW-DESCRIPTION');
        if (isHouseWithUWIAndBuildingReport(coverable))
            return getHippoText('DSS-RECEIPT-UW-AND-BUILDING-REPORT-DESCRIPTION');
        if (shouldShowUvpWarning(coverable))
            return getHippoText('DSS-RECEIPT-CARAVAN-NO-LICENSE-PLATE');
        return getHippoText('DSS-RECEIPT-UW-DESCRIPTION');
    };

    const renderInsuranceValidText = (coverable: CoverableDTO): React.JSX.Element => {
        if (coverable.willSpinOff) {
            return <></>;
        } else if (coverable.status === 'Removed') {
            return (
                <div className="paragraph--small u-Space-up">
                    {getHippoText('DSS-RECEIPT-PAGE-CANCELLATION-EFFECTIVE-DATE', {
                        date: toLongDateString(coverable.cancellationDate)
                    })}
                </div>
            );
        } else if (coverable.status === 'Changed') {
            return (
                <div className="paragraph--small u-Space-up">
                    {getHippoText('DSS-SHOPPING-BASKET-CHANGE-SUMMARY-FORWARD-DATE', {
                        date_value: toLongDateString(coverable.effectiveDate)
                    })}
                </div>
            );
        } else {
            return (
                <div className="paragraph--small u-Space-up">
                    {getHippoText('DSS-RECEIPT-PAGE-INSURANCE-VALID-TEXT')}{' '}
                    {toLongDateString(
                        coverableEffectiveDate(coverable, coverable.effectiveDate ?? {})
                    )}
                </div>
            );
        }
    };

    return (
        <>
            {coverables.map((coverable, index) => {
                const otherCompanyName = coverableToExternalCase(coverable, externalCases)
                    ?.otherCompany?.name;
                return (
                    <div key={coverable.fixedId} className="u-spacing-inset-m--stretch-max">
                        <div className="u-flex-box u-flex-box--space-between u-flex-box--nowrap">
                            <h6 className="u-spacing-stack-s">{coverable.flavourName}</h6>
                            {coverable.status !== 'Removed' && (
                                <h6 className="u-spacing-stack-s u u-text-right">
                                    <Currency
                                        price={
                                            selectedPaymentFrequency === paymentFrequencyMap.monthly
                                                ? coverable.monthlyPrice?.amount ?? 0
                                                : coverable.yearlyPrice?.amount ?? 0
                                        }
                                        paymentFrequencyPostfix={paymentFrequencyPostfix}
                                    />
                                </h6>
                            )}
                        </div>
                        <div className="paragraph--small u-color-secondary-4-medium">
                            {coverable.name}
                        </div>
                        {renderInsuranceValidText(coverable)}
                        {otherCompanyName && !coverable.willSpinOff && (
                            <div className="paragraph--small u-color-secondary-4-medium">
                                {`Samtidig opsiger vi forsikringen i ${otherCompanyName} fra ${toLongDateString(
                                    coverable.effectiveDate
                                )}`}
                            </div>
                        )}
                        {isAccCoverableForOtherPerson(coverable) ? (
                            <div className="u-Space-up">
                                <SpinOffMessage
                                    coverable={coverable}
                                    message={getHippoText('DSS-RECEIPT-OFFER-FOR-OTHER-LABEL', {
                                        Coverable_name: coverable.name ?? ''
                                    })}
                                />
                            </div>
                        ) : (
                            <div className="u-Space-up">
                                <SpinOffMessage
                                    coverable={coverable}
                                    message={SpinOffDescriptionText(coverable)}
                                />
                            </div>
                        )}
                        {shouldShowConditionReportWarning(coverable) &&
                            coverable.lobCode === LOB_CODE.PRIVATE_HOUSE &&
                            !(coverable as PrhCommonDTO).hasUWI && (
                                <Message theme="warning">
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(
                                                getHippoText(
                                                    'DSS-RECEIPT-UW-AND-BUILDING-REPORT-DESCRIPTION'
                                                )
                                            )
                                        }}
                                    />
                                </Message>
                            )}
                        {index !== lastIteration && (
                            <div className="u-Space-up">
                                <hr className="u-spacing-stack-none" />
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );
};

export default CoverableInfoList;
